/**
 * Compliance agreement in words — proposal §6 second-release gate 3, IMPLEMENTATION.md decisions 52 and 53.
 *
 * One wording for the Verdicts screen, a verdict's Compliance review, the Compliance agreement
 * dashboard and the Excel pane. **The figure is never rounded up to one the counts do not
 * reach**: 99.96% reads 99.9%, because 100% beside an override would be the one misleading
 * number on the page.
 */

import type { AgreementHeadline, ComplianceAnswer, OverrideCause, ReviewOutcome, VerdictState } from './api'

export function agreementPercent(ratio: number): string {
  if (ratio >= 1) return '100%'
  const tenths = Math.floor(ratio * 1000) / 10
  return `${Number.isInteger(tenths) ? tenths : tenths.toFixed(1)}%`
}

export function agreementText(figure: Pick<AgreementHeadline, 'ratio' | 'compared'>): string {
  return `Compliance agrees with ${agreementPercent(figure.ratio)} of ${figure.compared} verdict${
    figure.compared === 1 ? '' : 's'
  } reviewed`
}

export const ANSWER_LABEL: Record<ComplianceAnswer, string> = {
  within: 'Within the limit',
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
}

export const CAUSE_LABEL: Record<OverrideCause, string> = {
  limit: 'GUARD’s limit is recorded wrong',
  data: 'The holdings data is missing or wrong',
  reading: 'Compliance reads the limit differently',
  accepted: 'An accepted difference',
}

export const OUTCOME_LABEL: Record<ReviewOutcome, string> = {
  agreed: 'Agreed',
  overridden: 'Overridden',
  decided_by_compliance: 'Decided by compliance',
  read_by_a_person: 'Read by compliance',
  awaiting_review: 'Awaiting review',
  changed_since_review: 'Changed since review',
}

/** The badge colours, as verdicts use them: an override is as loud as a breach. */
export const OUTCOME_BADGE: Record<ReviewOutcome, string> = {
  agreed: 'pass',
  overridden: 'breach',
  decided_by_compliance: 'not_evaluable',
  read_by_a_person: 'pass',
  awaiting_review: 'not_run',
  changed_since_review: 'warn',
}

/** GUARD's verdict as an answer to compliance's question. A warning is inside the limit. */
export function answerOf(state: VerdictState): ComplianceAnswer {
  if (state === 'breach') return 'breach'
  if (state === 'not_evaluable') return 'not_evaluable'
  return 'within'
}
